import React from "react";
import {
  Box,
  Typography,
  Fab,
  Badge,
  Drawer,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChatIcon from "@mui/icons-material/Chat";
import HistoryIcon from "@mui/icons-material/History";
import ChatWindow from "../../Helpers/ChatWindow";

function EndorsementLogItem({ log }) {
  return (
    <Box sx={{ position: "relative", pl: 4, mb: 2 }}>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 12,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundImage: "linear-gradient(135deg,#3b82f6 0%,#06b6d4 100%)",
        }}
      />
      <Box sx={{ borderLeft: "2px solid #e5e7eb", ml: 1, pl: 2 }}>
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            p: 1.5,
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        >
          <Typography variant="body1" fontWeight="bold" sx={{ fontSize: "1rem" }}>
            {log.endorsement_type}
          </Typography>
          <Typography variant="body2" sx={{ color: "gray", fontSize: "0.95rem" }}>
            {new Date(log.endorsed_at).toLocaleString()}
          </Typography>
          {(log.contact_name || log.contact_email) && (
            <Typography
              variant="body2"
              sx={{ color: "#374151", fontSize: "0.95rem", mt: 0.5 }}
            >
              Contact: {log.contact_name || ""}
              {log.contact_name && log.contact_email ? " - " : ""}
              {log.contact_email || ""}
            </Typography>
          )}
          <Typography mt={1} sx={{ fontSize: "0.98rem" }}>
            {log.endorsement_description}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function renderEndorsementHistoryContent(isHistoryLoading, endorsementLogs) {
  if (isHistoryLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (endorsementLogs.length === 0) {
    return <Typography>No endorsements found.</Typography>;
  }

  return endorsementLogs.map((log, index) => (
    <EndorsementLogItem
      key={log.id ?? `endorse-${log.endorsed_at}-${index}`}
      log={log}
    />
  ));
}

function SimpleBondUpdateItem({ update: u }) {
  return (
    <Box key={u.bond_update_id} sx={{ position: "relative", pl: 4, mb: 2 }}>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 6,
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: "#10b981",
        }}
      />
      <Box sx={{ borderLeft: "2px solid #e5e7eb", ml: 1, pl: 2 }}>
        <Typography variant="body2" sx={{ color: "gray", fontSize: "0.95rem" }}>
          {new Date(u.created_at).toLocaleString()}
        </Typography>
        <Typography variant="body1" sx={{ fontSize: "1rem" }}>
          <strong>{u.requester_name}</strong> changed <strong>{u.field_name}</strong>
        </Typography>
        <Typography variant="body2" sx={{ fontSize: "0.98rem" }}>
          {String(u.old_value ?? "")} to <strong>{String(u.new_value ?? "")}</strong>
        </Typography>
      </Box>
    </Box>
  );
}

function CardBondUpdateItem({ update: u }) {
  return (
    <Box key={u.bond_update_id} sx={{ position: "relative", pl: 4, mb: 2 }}>
      <Box
        sx={{
          position: "absolute",
          left: 8,
          top: 12,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundImage:
            "linear-gradient(135deg,#10b981 0%,#22c55e 100%)",
        }}
      />
      <Box sx={{ borderLeft: "2px solid #e5e7eb", ml: 1, pl: 2 }}>
        <Box
          sx={{
            backgroundColor: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            p: 1.5,
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
          }}
        >
          <Typography variant="body2" sx={{ color: "gray", fontSize: "0.95rem" }}>
            {new Date(u.created_at).toLocaleString()}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: "1rem" }}>
            <strong>{u.requester_name}</strong> changed{" "}
            <Box
              component="span"
              sx={{
                px: 0.7,
                py: 0.1,
                backgroundColor: "#eef2ff",
                color: "#3730a3",
                borderRadius: 1,
                fontWeight: 600,
              }}
            >
              {u.field_name}
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.98rem" }}>
            {String(u.old_value ?? "")} to <strong>{String(u.new_value ?? "")}</strong>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Chat FAB, history FAB, ChatWindow, and history Drawer overlays.
 */
export function BondRetrievalChatHistoryOverlays({
  role,
  bond_id,
  username,
  openChat,
  setOpenChat,
  chatUnreadTotal,
  isHistoryOpen,
  setIsHistoryOpen,
  endorsementLogs,
  isHistoryLoading,
  historyTab,
  setHistoryTab,
  bondUpdates,
  toggleHistoryDrawer,
  historyUpdatesVariant = "simple",
}) {
  const showChat =
    role === "carrier_team_member" ||
    role === "super_user" ||
    role === "internal_user";

  const sortedUpdates = Array.isArray(bondUpdates)
    ? bondUpdates
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : [];

  const UpdateItem =
    historyUpdatesVariant === "card" ? CardBondUpdateItem : SimpleBondUpdateItem;

  return (
    <>
      {showChat && (
        <Box sx={{ position: "fixed", bottom: 16, right: 16, zIndex: 1500 }}>
          <Badge
            overlap="circular"
            color="success"
            invisible={!chatUnreadTotal}
            badgeContent={chatUnreadTotal}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{
              "& .MuiBadge-badge": {
                top: -6,
                right: -6,
                zIndex: 2,
                transform: "none",
                minWidth: 18,
                height: 18,
                fontSize: 11,
                fontWeight: 600,
                boxShadow: 1,
              },
            }}
          >
            <Fab
              aria-label="chat"
              sx={{
                background: "linear-gradient(135deg, #111 0%, #333 100%)",
                color: "#fff",
                border: "1px solid #444",
                boxShadow: 6,
                "&:hover": {
                  background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)",
                },
              }}
              onClick={() => setOpenChat(!openChat)}
            >
              {openChat ? (
                <CloseIcon sx={{ color: "#fff" }} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.2,
                  }}
                >
                  <ChatIcon sx={{ color: "#fff" }} />
                  <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
                    UW
                  </Typography>
                </Box>
              )}
            </Fab>
          </Badge>
        </Box>
      )}

      <Fab
        aria-label="history"
        sx={{
          position: "fixed",
          bottom: 16,
          right: 84,
          background: "linear-gradient(135deg, #111 0%, #333 100%)",
          color: "#fff",
          border: "1px solid #444",
          boxShadow: 6,
          "&:hover": {
            background: "linear-gradient(135deg, #1a1a1a 0%, #4d4d4d 100%)",
          },
        }}
        onClick={toggleHistoryDrawer}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 0.2,
          }}
        >
          <HistoryIcon sx={{ color: "#fff" }} />
          <Typography variant="body1" sx={{ fontSize: 8, color: "#fff" }}>
            History
          </Typography>
        </Box>
      </Fab>

      {openChat && showChat && (
        <ChatWindow
          username={username}
          bondId={bond_id}
          role={role}
          onClose={() => setOpenChat(false)}
        />
      )}

      <Drawer
        anchor="right"
        open={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        slotProps={{ paper: { sx: { width: 560, backgroundColor: "#f8fafc" } } }}
      >
        <Box p={3}>
          <Tabs
            value={historyTab}
            onChange={(e, v) => setHistoryTab(v)}
            variant="fullWidth"
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": { height: 3, borderRadius: 1 },
            }}
          >
            <Tab label="Endorsements" sx={{ fontSize: "0.95rem" }} />
            <Tab label="Updates" sx={{ fontSize: "0.95rem" }} />
          </Tabs>

          {historyTab === 0 && (
            <Box mt={2}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: "1.15rem" }}
              >
                Endorsement History
              </Typography>
              {renderEndorsementHistoryContent(isHistoryLoading, endorsementLogs)}
            </Box>
          )}

          {historyTab === 1 && (
            <Box mt={2}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ fontSize: "1.15rem" }}
              >
                Bond Updates
              </Typography>
              {sortedUpdates.length > 0 ? (
                sortedUpdates.map((u) => <UpdateItem key={u.bond_update_id} update={u} />)
              ) : (
                <Typography
                  sx={
                    historyUpdatesVariant === "card" ? { color: "#6b7280" } : undefined
                  }
                >
                  No updates found.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default BondRetrievalChatHistoryOverlays;
